import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import {
  RefreshCw, Plus, X, Trash2, Power,
  Chrome, Cable, AlertCircle, Search, Upload, ImageOff,
} from "lucide-react";

// Read a File as a base64 data URI. Returns a Promise<string|null>.
function fileToDataUri(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(typeof fr.result === "string" ? fr.result : null);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

const SOURCE_META = {
  extension: { label: "Extension", Icon: Chrome, cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  api:       { label: "API",       Icon: Cable, cls: "text-violet-700 bg-violet-50 border-violet-200" },
};

const CATEGORIES = ["airline", "hotel", "credit_card", "bank", "retail", "other"];

function SourceBadge({ source }) {
  const meta = SOURCE_META[source];
  if (!meta) return null;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${meta.cls}`}>
      <Icon size={11} /> {meta.label}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function AddRowForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    code: "", name: "", category: "other", currency: "",
    recipe_code: "", api_name: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState("");

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const payload = { ...form };
      // Drop empty optional fields so backend stores null instead of "".
      for (const k of ["recipe_code", "api_name", "currency"]) {
        if (!payload[k]) delete payload[k];
      }
      const { data } = await api.post("/admin/master-list", payload);
      onCreated(data.program);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "create failed");
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="card p-5 mt-4 space-y-4" data-testid="master-list-create-form">
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-[14px]">New master-list entry</div>
        <button type="button" onClick={onCancel} className="text-neutral-400 hover:text-neutral-700"><X size={16} /></button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">Code</div>
          <input
            value={form.code} onChange={(e) => set("code", e.target.value)}
            placeholder="united_airlines" required pattern="[a-z0-9][a-z0-9_-]+"
            className="w-full px-3 py-2 text-[13px] rounded-md border border-black/10 focus:border-black/40 outline-none"
            data-testid="master-form-code"
          />
        </label>
        <label className="block">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">Display name</div>
          <input
            value={form.name} onChange={(e) => set("name", e.target.value)}
            placeholder="United Airlines MileagePlus" required
            className="w-full px-3 py-2 text-[13px] rounded-md border border-black/10 focus:border-black/40 outline-none"
            data-testid="master-form-name"
          />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">Category</div>
          <select
            value={form.category} onChange={(e) => set("category", e.target.value)}
            className="w-full px-3 py-2 text-[13px] rounded-md border border-black/10 focus:border-black/40 outline-none"
            data-testid="master-form-category"
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">Currency (optional)</div>
          <input
            value={form.currency} onChange={(e) => set("currency", e.target.value)}
            placeholder="miles" className="w-full px-3 py-2 text-[13px] rounded-md border border-black/10 focus:border-black/40 outline-none"
          />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">Recipe code (extension)</div>
          <input
            value={form.recipe_code} onChange={(e) => set("recipe_code", e.target.value)}
            placeholder="united"
            className="w-full px-3 py-2 text-[13px] rounded-md border border-black/10 focus:border-black/40 outline-none font-mono"
          />
        </label>
        <label className="block">
          <div className="text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 mb-1">API name (future)</div>
          <input
            value={form.api_name} onChange={(e) => set("api_name", e.target.value)}
            placeholder=""
            className="w-full px-3 py-2 text-[13px] rounded-md border border-black/10 focus:border-black/40 outline-none font-mono"
          />
        </label>
      </div>

      {err && (
        <div className="text-[12.5px] text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn btn-ghost text-[12.5px]">Cancel</button>
        <button type="submit" disabled={busy} className="btn btn-primary text-[12.5px] inline-flex items-center gap-1.5" data-testid="master-form-submit">
          {busy ? <><span className="spinner" /> Creating…</> : <><Plus size={13} /> Create</>}
        </button>
      </div>
    </form>
  );
}

function MasterRow({ row, onChanged, onOpenLogo }) {
  const [busy, setBusy] = useState("");
  const [err, setErr]   = useState("");

  async function toggle() {
    setBusy("toggle"); setErr("");
    try {
      const { data } = await api.patch(`/admin/master-list/${row.id}`, { enabled: !row.enabled });
      onChanged(data.program);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "update failed");
    } finally { setBusy(""); }
  }

  async function remove() {
    if (!window.confirm(`Delete master-list entry for ${row.name}?`)) return;
    setBusy("delete"); setErr("");
    try {
      await api.delete(`/admin/master-list/${row.id}`);
      onChanged(null, row.id);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "delete failed");
    } finally { setBusy(""); }
  }

  async function uploadLogo(file) {
    if (!file) return;
    if (file.size > 150_000) {
      setErr("Logo too large (>150 KB). Use a smaller PNG / SVG.");
      return;
    }
    setBusy("logo"); setErr("");
    try {
      const data_uri = await fileToDataUri(file);
      const { data } = await api.put(`/admin/master-list/${row.id}/logo`, { data_uri });
      onChanged(data.program);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "logo upload failed");
    } finally { setBusy(""); }
  }

  async function clearLogo() {
    setBusy("logo"); setErr("");
    try {
      const { data } = await api.put(`/admin/master-list/${row.id}/logo`, { data_uri: null });
      onChanged(data.program);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "logo clear failed");
    } finally { setBusy(""); }
  }

  const hasLogo = !!row.custom_logo;

  return (
    <div className={`grid grid-cols-12 items-center px-5 py-3.5 border-b border-black/5 last:border-b-0 text-[13px] ${row.enabled ? "" : "opacity-50"}`} data-testid={`master-row-${row.code}`}>
      <div className="col-span-3 min-w-0 flex items-center gap-2.5">
        {hasLogo ? (
          <button
            type="button"
            onClick={() => onOpenLogo?.({ src: row.custom_logo, name: row.name, code: row.code })}
            title="Click to preview"
            className="w-8 h-8 rounded bg-white border border-black/5 shrink-0 overflow-hidden hover:ring-2 hover:ring-black/20 transition"
            data-testid={`master-row-logo-preview-${row.code}`}
          >
            <img src={row.custom_logo} alt={row.name} className="w-full h-full object-contain" />
          </button>
        ) : (
          <div className="w-8 h-8 rounded bg-black/[0.04] border border-black/5 shrink-0 flex items-center justify-center text-neutral-400">
            <ImageOff size={13} />
          </div>
        )}
        <div className="min-w-0">
          <div className="font-semibold truncate">{row.name}</div>
          <div className="text-[11.5px] text-neutral-500 font-mono truncate">{row.code}</div>
        </div>
      </div>
      <div className="col-span-2 text-[12px] text-neutral-700">{row.category}</div>
      <div className="col-span-3 flex flex-wrap gap-1.5">
        {(row.sources || []).length === 0
          ? <span className="text-[11px] text-neutral-400 italic">no sources linked</span>
          : row.sources.map((s) => <SourceBadge key={s} source={s} />)}
      </div>
      <div className="col-span-2 text-[11px] text-neutral-500">{fmtDate(row.updated_at)}</div>
      <div className="col-span-2 flex items-center justify-end gap-1.5">
        <label
          title={hasLogo ? "Replace custom logo" : "Upload custom logo"}
          className="p-1.5 rounded-md hover:bg-black/[0.04] text-neutral-600 hover:text-black cursor-pointer"
        >
          {busy === "logo" ? <span className="spinner" /> : <Upload size={13} />}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
            onChange={(e) => uploadLogo(e.target.files?.[0])}
            disabled={!!busy}
            className="hidden"
            data-testid={`master-row-logo-${row.code}`}
          />
        </label>
        {hasLogo && (
          <button
            onClick={clearLogo} disabled={!!busy}
            title="Clear custom logo"
            className="p-1.5 rounded-md hover:bg-black/[0.04] text-neutral-400 hover:text-red-700"
          >
            <ImageOff size={13} />
          </button>
        )}
        <button
          onClick={toggle} disabled={!!busy}
          title={row.enabled ? "Disable in picker" : "Enable in picker"}
          className="p-1.5 rounded-md hover:bg-black/[0.04] text-neutral-600 hover:text-black"
        >
          {busy === "toggle" ? <span className="spinner" /> : <Power size={13} />}
        </button>
        <button
          onClick={remove} disabled={!!busy}
          title="Delete"
          className="p-1.5 rounded-md hover:bg-red-50 text-neutral-600 hover:text-red-700"
        >
          {busy === "delete" ? <span className="spinner" /> : <Trash2 size={13} />}
        </button>
        {err && <span className="text-[11px] text-red-600 ml-1" title={err}><AlertCircle size={12} /></span>}
      </div>
    </div>
  );
}

export default function AdminMasterList() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState("");
  const [flash, setFlash]       = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [syncingRecipes, setSyncingRecipes] = useState(false);
  const [q, setQ]               = useState("");
  const [filterSrc, setFilterSrc] = useState("");
  // { src, name, code } when the user clicks a row's logo thumbnail.
  const [logoPreview, setLogoPreview] = useState(null);

  async function refresh() {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/master-list");
      setRows(data.programs || []);
    } catch (e) {
      setErr(e?.response?.status === 403 ? "You don't have admin access." : (e?.message || "load failed"));
    } finally { setLoading(false); }
  }

  useEffect(() => { refresh(); }, []);

  async function syncRecipes() {
    setSyncingRecipes(true); setErr(""); setFlash("");
    try {
      const { data } = await api.post("/admin/master-list/sync-recipes");
      setFlash(`Recipe sync: ${data.created} created, ${data.linked} linked.`);
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "recipe sync failed");
    } finally { setSyncingRecipes(false); }
  }

  function onCreated(p) {
    setRows((prev) => [p, ...prev].sort((a, b) => a.name.localeCompare(b.name)));
    setShowCreate(false);
  }

  function onChanged(updated, deletedId) {
    if (deletedId) {
      setRows((prev) => prev.filter((r) => r.id !== deletedId));
      return;
    }
    if (!updated) return;
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  }

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (needle) {
        const hay = `${r.name} ${r.code} ${r.recipe_code || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (filterSrc) {
        if (!(r.sources || []).includes(filterSrc)) return false;
      }
      return true;
    });
  }, [rows, q, filterSrc]);

  const counts = useMemo(() => ({
    total: rows.length,
    ext: rows.filter((r) => r.recipe_code).length,
    api: rows.filter((r) => r.api_name).length,
  }), [rows]);

  return (
    <AdminLayout
      activeTab="master-list"
      right={
        <>
          <button onClick={refresh} disabled={loading} className="btn btn-ghost text-[12.5px] inline-flex items-center gap-1.5">
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary text-[12.5px] inline-flex items-center gap-1.5" data-testid="master-list-add">
            <Plus size={13} /> Add entry
          </button>
        </>
      }
    >
      <div data-testid="admin-master-list-page">
        <div className="section-eyebrow">Admin · Picker catalog</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Master program list</h1>
        <p className="text-[13.5px] text-neutral-600 mt-2 max-w-[720px]">
          One row per loyalty program. Each row links to a browser-extension recipe
          (or a direct API integration). This is the catalog the "Add program" picker
          iterates over.
        </p>

        <div className="flex flex-wrap gap-3 mt-6 text-[12.5px]">
          <div className="card px-4 py-2 text-neutral-700">Total <span className="font-semibold tabular ml-1">{counts.total}</span></div>
          <div className="card px-4 py-2 text-neutral-700"><Chrome size={11} className="inline mr-1" /> Extension <span className="font-semibold tabular ml-1">{counts.ext}</span></div>
          <div className="card px-4 py-2 text-neutral-700"><Cable size={11} className="inline mr-1" /> API <span className="font-semibold tabular ml-1">{counts.api}</span></div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-6">
          <button
            onClick={syncRecipes}
            disabled={syncingRecipes}
            className="btn btn-ghost text-[12.5px] inline-flex items-center gap-1.5"
            data-testid="master-list-sync-recipes"
          >
            {syncingRecipes ? <><span className="spinner" /> Syncing…</> : <><Chrome size={13} /> Sync extension recipes</>}
          </button>
          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search programs…"
                className="pl-7 pr-3 py-1.5 text-[12.5px] rounded-md border border-black/10 focus:border-black/40 outline-none w-56"
                data-testid="master-list-search"
              />
            </div>
            <select
              value={filterSrc} onChange={(e) => setFilterSrc(e.target.value)}
              className="text-[12.5px] rounded-md border border-black/10 focus:border-black/40 outline-none px-2 py-1.5"
              data-testid="master-list-filter-source"
            >
              <option value="">All sources</option>
              <option value="extension">Extension</option>
              <option value="api">API</option>
            </select>
          </div>
        </div>

        {flash && (
          <div className="mt-4 p-3 border rounded-[10px] text-[12.5px]" style={{ background: "var(--good-bg)", color: "var(--good)", borderColor: "var(--good-border)" }} data-testid="master-flash">
            {flash}
          </div>
        )}
        {err && (
          <div className="mt-4 p-3 border rounded-[10px] text-[13px]" style={{ background: "var(--bad-bg)", color: "var(--bad)", borderColor: "var(--bad-border)" }}>
            {err}
          </div>
        )}

        {showCreate && <AddRowForm onCreated={onCreated} onCancel={() => setShowCreate(false)} />}

        <div className="card overflow-hidden mt-5" data-testid="admin-master-list-table">
          <div className="grid grid-cols-12 px-5 py-3 text-[10.5px] uppercase tracking-[0.12em] text-neutral-500 border-b border-black/5 bg-black/[0.02]">
            <div className="col-span-3">Program</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-3">Sources</div>
            <div className="col-span-2">Updated</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {loading && rows.length === 0 ? (
            <div className="px-5 py-8 text-[13px] text-neutral-500 text-center"><span className="spinner inline-block mr-2" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="px-5 py-8 text-[13px] text-neutral-500 text-center">
              {rows.length === 0
                ? <>No entries yet — sync extension recipes or add one above.</>
                : <>No programs match the current filters.</>}
            </div>
          ) : (
            filtered.map((r) => (
              <MasterRow
                key={r.id}
                row={r}
                onChanged={onChanged}
                onOpenLogo={setLogoPreview}
              />
            ))
          )}
        </div>
      </div>

      {logoPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(10,10,10,0.55)" }}
          onClick={() => setLogoPreview(null)}
          data-testid="master-logo-preview-modal"
        >
          <div
            className="card p-6 max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLogoPreview(null)}
              className="absolute top-3 right-3 text-neutral-400 hover:text-black"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="font-display font-bold text-[15px] tracking-tight">{logoPreview.name}</div>
            <div className="text-[11px] text-neutral-500 font-mono mt-0.5">{logoPreview.code}</div>
            <div className="mt-5 flex items-center justify-center bg-black/[0.03] border border-black/5 rounded-[10px] p-6 min-h-[200px]">
              <img
                src={logoPreview.src}
                alt={logoPreview.name}
                className="max-w-full max-h-[300px] object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
